import parseContentDisposition from './parseContentDisposition.ts';
import {useCreateAuthenticatedFetch} from '../api/useCreateAuthenticatedFetch.ts';
import {handleResponseError} from '../api/handleResponseError.ts';

export interface DownloadProgress {
    loaded: number;
    total: number;
    percentage: number;
}

/**
 * Hand a blob to the browser as a download.
 *
 * Exported as a plain function as well as on the hook, because callers that build a file in
 * memory (see downloadCsv, downloadJson) need it outside a component and have no use for the
 * hook's authenticated fetch.
 */
export const triggerBlobDownload = (blob: Blob, filename: string): void => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

/**
 * Custom hook for downloading files with authentication and progress tracking
 */
export function useFileDownloader() {
    const authenticatedFetch = useCreateAuthenticatedFetch();

    /**
     * Downloads a file with progress tracking
     * @param url The URL to download from
     * @param onProgress Optional callback for progress updates
     * @returns Promise with blob and filename
     */
    const downloadFile = async (
        url: string,
        onProgress?: (progress: DownloadProgress) => void
    ): Promise<{blob: Blob; filename: string}> => {
        const response = await authenticatedFetch(url);

        if (!response.ok) {
            // Throws a ProblemDetailsError carrying whatever the server said. A download
            // endpoint that refuses a request (e.g. the Business Central document proxy
            // explaining why a record has no downloadable file) puts the reason in the
            // ProblemDetails body, and a fixed "unable to download" would hide it.
            // downloadErrorSummary turns the error into the sentence to show.
            await handleResponseError(response);
        }

        // Get filename from Content-Disposition header or fallback to URL
        const contentDisposition = response.headers.get('Content-Disposition');
        const filename = contentDisposition
            ? parseContentDisposition(contentDisposition)
            : url.split('/').pop() || 'download.file';

        // Get the total size from the headers if available
        const contentLength = response.headers.get('Content-Length');
        const total = contentLength ? parseInt(contentLength, 10) : 0;

        // If no progress callback or browser doesn't support ReadableStream, use simpler approach
        if (!onProgress || !response.body) {
            const blob = await response.blob();
            return {blob, filename};
        }

        const reader = response.body.getReader();
        const chunks: Uint8Array[] = [];
        let receivedLength = 0;

        // eslint-disable-next-line no-constant-condition
        while (true) {
            const {done, value} = await reader.read();

            if (done) {
                break;
            }

            if (value) {
                chunks.push(value);
                receivedLength += value.length;

                onProgress({
                    loaded: receivedLength,
                    total: total,
                    percentage:
                        total > 0
                            ? Math.round((receivedLength / total) * 100)
                            : 0
                });
            }
        }

        // Combine chunks into a single Blob
        const blob = new Blob(chunks as BlobPart[]);
        return {blob, filename};
    };

    /**
     * Helper function to trigger browser download
     */
    const triggerDownload = triggerBlobDownload;

    return {downloadFile, triggerDownload};
}
